import { Controller } from "@hotwired/stimulus"

import { clearPickerValueInputs } from "../utils/cloned_field_reset"
import { evenSplitMinutes, nextAutoIndex, restMinutesFor } from "../utils/multi_site_auto_fill"
import { limitPerEntryAllowanceHours } from "../utils/per_entry_allowance_limit"
import { setSubmitGate } from "../utils/submit_gate"

export default class extends Controller {
  static targets = [
    "netWorkTime",
    "allocationContainer",
    "allocation",
    "addSiteButton",
    "splitEvenlyButton",
    "totalAllocated",
    "remainingHours",
    "remainingLabel",
    "autoExtendHint",
    "creditedDriving",
    "creditedDrivingRow",
  ]
  static values = {
    maxSites: { type: Number, default: 5 },
    autoExtend: { type: Boolean, default: false },
    durationFormat: { type: String, default: "clock" },
  }

  connect() {
    this.allocationIndex = this.allocationTargets.length - 1
    this.autoAllocation = this.initialAutoAllocation()

    this._handleTimeChange = (event) => {
      const name = event.target?.name || ""
      if (name.includes("time_from") || name.includes("time_to") || name.includes("time_break")) {
        this.recalculate()
      }
    }

    // site_driving_time_controller is mounted on the FORM, i.e. an ancestor of this
    // element, so its event bubbles past us rather than through us — a data-action on
    // this element would never see it. Listen where it is actually dispatched.
    this._handleCreditChange = (event) => this.creditChanged(event)

    this._form = this.element.closest("form")
    if (this._form) {
      this._form.addEventListener("change", this._handleTimeChange)
      this._form.addEventListener("input", this._handleTimeChange)
      this._form.addEventListener("site-driving-time:credit-changed", this._handleCreditChange)
    }

    // Delay initial calculation to let time input controllers initialize
    setTimeout(() => this.recalculate(), 150)
  }

  disconnect() {
    if (this._form) {
      this._form.removeEventListener("change", this._handleTimeChange)
      this._form.removeEventListener("input", this._handleTimeChange)
      this._form.removeEventListener("site-driving-time:credit-changed", this._handleCreditChange)
      // Release this controller's own gate; another may still be holding one.
      setSubmitGate(this._form, "multi-site", false)
    }
  }

  recalculate() {
    const netMinutes = this.calculateNetWorkMinutes()
    const netHours = netMinutes / 60

    if (netMinutes > 0) {
      this.netWorkTimeTarget.textContent = this.formatHours(netHours)
      this.netWorkTimeTarget.closest("[data-net-work-banner]")?.classList.remove("hidden")
    } else {
      this.netWorkTimeTarget.textContent = "--"
    }

    this.updateTotals()
  }

  addSite() {
    const firstAllocation = this.allocationTargets[0]
    if (!firstAllocation) return

    this.allocationIndex++
    const clone = firstAllocation.cloneNode(true)

    this.rewriteClonedAllocation(clone, this.allocationIndex)
    this.clearAllocationValues(clone)
    this.resetSlimSelects(clone)

    this.allocationContainerTarget.appendChild(clone)
    // Typed-in rows leave nothing to follow, so the new row takes the rest. While
    // another row is still automatic the new one starts blank: its typed hours then
    // come out of that row.
    if (!this.autoAllocation) this.autoAllocation = clone

    if (this.allocationTargets.length >= this.maxSitesValue) {
      this.addSiteButtonTarget.classList.add("hidden")
    }

    this.updateTotals()
  }

  removeSite(event) {
    const allocation = event.target.closest("[data-multi-site-booking-target='allocation']")
    if (!allocation) return

    // Don't remove the last allocation
    if (this.allocationTargets.length <= 1) return

    allocation.remove()
    if (allocation === this.autoAllocation)
      this.autoAllocation = this.blankAllocationOtherThan(null)
    this.reindexAllocations()
    this.addSiteButtonTarget.classList.remove("hidden")
    this.updateTotals()
  }

  // Per-allocation Fahrzeiten rows (only present when the template configures
  // driving times per entry). Each allocation carries its own row container and
  // an inert <template> whose NEW_INDEX placeholder is stamped with a unique id.
  addDrivingTime(event) {
    const allocation = event.target.closest("[data-multi-site-booking-target='allocation']")
    if (!allocation) return

    const tpl = allocation.querySelector("[data-allocation-driving-template]")
    const container = allocation.querySelector("[data-allocation-driving-rows]")
    if (!tpl || !container) return

    const html = tpl.innerHTML.replaceAll("NEW_INDEX", String(new Date().getTime()))
    container.insertAdjacentHTML("beforeend", html)
  }

  removeDrivingTime(event) {
    const row = event.target.closest("[data-allocation-driving-row]")
    if (row) row.remove()
  }

  // Typing into a row makes it manual. When that row was the automatic one, the role
  // passes to another row that is still blank, so it shows what is left to book.
  hoursEdited(event) {
    const allocation = event.target.closest("[data-multi-site-booking-target='allocation']")
    if (allocation && allocation === this.autoAllocation) {
      this.autoAllocation = this.blankAllocationOtherThan(allocation)
    }
    this.updateTotals()
  }

  // Hands the "follows the remainder" role to +allocation+ — the voice input's
  // "den Rest". Typing into another row moves it on as usual (#hoursEdited).
  followRemainder(allocation) {
    if (!this.allocationTargets.includes(allocation)) return
    this.autoAllocation = allocation
    this.updateTotals()
  }

  // "Rest übernehmen": this row takes whatever the others leave of the day.
  takeRest(event) {
    const allocation = event.target.closest("[data-multi-site-booking-target='allocation']")
    const index = this.allocationTargets.indexOf(allocation)
    if (index < 0) return

    const minutes = restMinutesFor({
      targetMinutes: this.targetMinutes(),
      rowMinutes: this.allocationTargets.map((alloc) => this.allocationMinutes(alloc)),
      index,
    })
    this.writeAllocationMinutes(allocation, minutes)
    this.updateTotals()
  }

  // "Gleichmäßig aufteilen": every row gets an equal share and becomes manual.
  splitEvenly() {
    const shares = evenSplitMinutes(this.targetMinutes(), this.allocationTargets.length)
    if (shares.length === 0) return

    this.autoAllocation = null
    this.allocationTargets.forEach((alloc, i) => this.writeAllocationMinutes(alloc, shares[i]))
    this.updateTotals()
  }

  updateTotals() {
    // Vergütete Fahrzeiten are booked onto the driving Lohnart rather than onto a
    // Baustelle. Whether they also shrink what has to be spread across the sites depends
    // on the mode: carved out of the window they do, paid on top of it they do not — the
    // sites then cover the whole window on their own. The emitting controller already
    // resolved that, so this one only distinguishes "shown to the user" (creditMinutes)
    // from "has to fit inside the window" (insideWindowMinutes). Mirrors
    // ConfigurableTimeSheets::MultiSiteTimesheetCreator#validate_total_matches — the two
    // must agree, or a form that reads balanced is rejected on submit.
    const creditMinutes = this.creditedDrivingMinutes()
    const insideWindowMinutes = this.creditedDrivingMinutesInsideWindow()

    // Fill the rest row before anything sums the rows: it can only ever be what the
    // others leave of the window, so the auto-extend below fires only once the typed
    // rows on their own outgrow the window — the rest row gives way first.
    this.applyAutoFill()

    // "Pro Eintrag" Zulagen: a site's hours cap what can be entered for it.
    this.allocationTargets.forEach((alloc) =>
      limitPerEntryAllowanceHours(alloc, this.allocationMinutes(alloc)),
    )

    const totalMinutes = this.allocationTargets.reduce(
      (sum, alloc) => sum + (this.allocationMinutes(alloc) || 0),
      0,
    )

    // Auto-extend: when over-allocated, push the end time out so net == allocated.
    // The write dispatches a change event, which re-runs the calculation with the
    // new window, so bail out of this pass once it fires.
    if (this.autoExtendValue && this.applyAutoExtend(totalMinutes + insideWindowMinutes)) return

    const totalHours = totalMinutes / 60
    const netMinutes = this.calculateNetWorkMinutes()
    const remainingMinutes = netMinutes - totalMinutes - insideWindowMinutes
    const balanced = Math.abs(remainingMinutes) <= 1

    if (this.hasTotalAllocatedTarget) {
      this.totalAllocatedTarget.textContent = this.formatHours(totalHours)
    }

    this.renderCreditedDriving(creditMinutes)

    if (this.hasRemainingHoursTarget) {
      this.remainingHoursTarget.textContent = this.formatHours(Math.abs(remainingMinutes / 60))

      const badge = this.remainingHoursTarget.closest("[data-remaining-badge]")
      if (badge) {
        badge.classList.remove(
          "text-green-700",
          "bg-green-50",
          "text-red-700",
          "bg-red-50",
          "text-amber-700",
          "bg-amber-50",
          "bg-gray-100",
          "text-gray-600",
        )
        if (balanced) {
          badge.classList.add("text-green-700", "bg-green-50")
        } else if (remainingMinutes < 0) {
          badge.classList.add("text-red-700", "bg-red-50")
        } else {
          badge.classList.add("text-green-700", "bg-green-50")
        }

        // Update label: "Verbleibend" vs "Zu viel"
        if (this.hasRemainingLabelTarget) {
          this.remainingLabelTarget.textContent =
            remainingMinutes < 0 ? badge.dataset.labelOver : badge.dataset.labelRemaining
        }
      }
    }

    this.renderAutoFillControls(balanced, netMinutes)

    // Disable/enable submit button
    this.toggleSubmit(balanced || netMinutes === 0)
  }

  applyAutoFill() {
    if (!this.autoAllocation || !this.autoAllocation.isConnected) return

    // No window yet: leave the row blank rather than booking 0h onto it.
    if (this.calculateNetWorkMinutes() === 0) {
      this.writeAllocationValue(this.autoAllocation, "")
      return
    }

    const minutes = restMinutesFor({
      targetMinutes: this.targetMinutes(),
      rowMinutes: this.allocationTargets.map((alloc) => this.allocationMinutes(alloc)),
      index: this.allocationTargets.indexOf(this.autoAllocation),
    })
    this.writeAllocationMinutes(this.autoAllocation, minutes)
  }

  renderAutoFillControls(balanced, netMinutes) {
    this.allocationTargets.forEach((alloc) => {
      const auto = alloc === this.autoAllocation
      alloc.querySelector("[data-auto-badge]")?.classList.toggle("hidden", !auto)
      alloc
        .querySelector("[data-take-rest]")
        ?.classList.toggle("hidden", auto || balanced || netMinutes === 0)
    })

    if (this.hasSplitEvenlyButtonTarget) {
      this.splitEvenlyButtonTarget.classList.toggle(
        "hidden",
        this.allocationTargets.length < 2 || netMinutes === 0,
      )
    }
  }

  // What the sites have to cover: the net window minus credited driving inside it.
  targetMinutes() {
    return this.calculateNetWorkMinutes() - this.creditedDrivingMinutesInsideWindow()
  }

  // Round per allocation, exactly like MultiSiteTimesheetCreator#allocated_minutes.
  // Hours carry two decimals, so an unrounded 4.82h is 289.2min — 0.2min "over" the
  // 289min window it was derived from, which is enough to trip auto-extend. `null`
  // while the row is blank.
  allocationMinutes(allocation) {
    const value = allocation.querySelector("input[name$='[hours]']")?.value
    if (!value) return null
    const hours = parseFloat(value)
    return Number.isNaN(hours) ? null : Math.round(hours * 60)
  }

  writeAllocationMinutes(allocation, minutes) {
    this.writeAllocationValue(allocation, String(Math.round((minutes / 60) * 100) / 100))
  }

  // Written without an event on purpose: an `input` would mark the row manual.
  writeAllocationValue(allocation, value) {
    const input = allocation.querySelector("input[name$='[hours]']")
    if (input && input.value !== value) input.value = value
  }

  // A one-row form starts on its rest row unless the row was re-rendered after a
  // failed save with typed hours. Otherwise a blank row, if any, takes the rest.
  initialAutoAllocation() {
    const rows = this.allocationTargets
    if (
      rows.length === 1 &&
      (rows[0].dataset.autoCandidate === "true" || this.allocationMinutes(rows[0]) == null)
    ) {
      return rows[0]
    }
    return this.blankAllocationOtherThan(null)
  }

  blankAllocationOtherThan(excluded) {
    const rows = this.allocationTargets.filter((alloc) => alloc !== excluded)
    const index = nextAutoIndex(rows.map((alloc) => this.allocationMinutes(alloc)))
    return index < 0 ? null : rows[index]
  }

  // Announced by site_driving_time_controller whenever a direction is ticked or a row's
  // figure is refetched. That controller owns the minutes; this one only spends them.
  creditChanged(event) {
    this._creditMinutes = Number(event?.detail?.creditMinutes) || 0
    this._creditMinutesInsideWindow = Number(event?.detail?.minutesInsideWindow) || 0
    this.updateTotals()
  }

  creditedDrivingMinutes() {
    return this._creditMinutes || 0
  }

  // The share of the credit that has to fit inside the entered window: everything when it
  // is carved out, nothing when it is paid on top.
  creditedDrivingMinutesInsideWindow() {
    return this._creditMinutesInsideWindow || 0
  }

  renderCreditedDriving(minutes) {
    if (this.hasCreditedDrivingTarget) {
      this.creditedDrivingTarget.textContent = this.formatHours(minutes / 60)
    }
    // The line is noise on a day with no credited drive.
    if (this.hasCreditedDrivingRowTarget) {
      this.creditedDrivingRowTarget.classList.toggle("hidden", minutes <= 0)
    }
  }

  // A named gate rather than writing `disabled` directly: break_rule_controller has
  // its own reason to stop the submission, and whichever wrote last used to win —
  // clearing the break shortfall re-enabled a button this allocation still wants
  // disabled. The gate also covers every submit button, not just the first: the
  // offcanvas drawer renders its own.
  toggleSubmit(enabled) {
    setSubmitGate(this._form, "multi-site", !enabled)
  }

  // Private helpers

  calculateNetWorkMinutes() {
    const form = this.element.closest("form")
    if (!form) return 0

    const timeFrom = this.readTimeValue(form, "time_from")
    const timeTo = this.readTimeValue(form, "time_to")
    const timeBreak = this.readTimeValue(form, "time_break")

    if (!timeFrom || !timeTo) return 0

    const fromMinutes = this.parseTimeToMinutes(timeFrom)
    let toMinutes = this.parseTimeToMinutes(timeTo)
    const breakMinutes = timeBreak ? this.parseTimeToMinutes(timeBreak) : 0

    // Handle 00:00 as end-of-day
    if (toMinutes === 0 && fromMinutes > 0) toMinutes = 1440

    const net = toMinutes - fromMinutes - breakMinutes
    return Math.max(net, 0)
  }

  // Extend-only auto-adjust: when the allocated minutes exceed the entered time
  // window, grow `time_to` so net work time equals the allocation total. Never
  // shrinks the window. Returns true when it wrote the field (a recalculation is
  // then in flight via the dispatched change event).
  //
  // `totalMinutes` is what the window has to cover: the allocated site hours PLUS any
  // credited driving time, because that too is booked inside the window. Mirrors
  // MultiSiteTimesheetCreator#apply_auto_extend_end_time.
  applyAutoExtend(totalMinutes) {
    const form = this.element.closest("form")
    if (!form) return false

    const timeFrom = this.readTimeValue(form, "time_from")
    const timeTo = this.readTimeValue(form, "time_to")
    if (!timeFrom || !timeTo) {
      this.toggleAutoExtendHint(false)
      return false
    }

    const fromMinutes = this.parseTimeToMinutes(timeFrom)
    let toMinutes = this.parseTimeToMinutes(timeTo)
    if (toMinutes === 0 && fromMinutes > 0) toMinutes = 1440
    // Leave overnight windows alone — they aren't supported for multi-site.
    if (toMinutes < fromMinutes) {
      this.toggleAutoExtendHint(false)
      return false
    }

    const breakValue = this.readTimeValue(form, "time_break")
    const breakMinutes = breakValue ? this.parseTimeToMinutes(breakValue) : 0
    const targetToMinutes = fromMinutes + breakMinutes + totalMinutes

    // Extend only within the same day. Crossing midnight (>= 1440) is an overnight
    // shift, which multi-site booking doesn't support — leave the over warning.
    if (targetToMinutes > toMinutes && targetToMinutes < 1440) {
      this.autoExtendedToMinutes = targetToMinutes
      this.toggleAutoExtendHint(true)
      return this.writeTimeValue(form, "time_to", targetToMinutes)
    }

    // No extension needed this pass; keep the hint only while the end time is
    // still sitting at a value we previously auto-extended to.
    this.toggleAutoExtendHint(
      this.autoExtendedToMinutes != null && toMinutes === this.autoExtendedToMinutes,
    )
    return false
  }

  toggleAutoExtendHint(show) {
    if (this.hasAutoExtendHintTarget) {
      this.autoExtendHintTarget.classList.toggle("hidden", !show)
    }
  }

  writeTimeValue(form, fieldSuffix, minutes) {
    const timeStr = this.minutesToTime(minutes)
    const selectors = [
      `input[type="hidden"][name$="[${fieldSuffix}]"][data-time-stepper-target="hidden"]`,
      `input[type="hidden"][name$="[${fieldSuffix}]"][data-duration-input-target="hidden"]`,
      `input[type="time"][name$="[${fieldSuffix}]"]`,
      `input[type="text"][name$="[${fieldSuffix}]"]`,
      `input[type="hidden"][name$="[${fieldSuffix}]"]`,
    ]
    for (const selector of selectors) {
      const input = form.querySelector(selector)
      if (input) {
        if (input.value === timeStr) return false
        input.value = timeStr
        // The time-stepper syncs its display and the form recalculates off `change`.
        input.dispatchEvent(new Event("change", { bubbles: true }))
        return true
      }
    }
    return false
  }

  minutesToTime(minutes) {
    // Round before formatting: a fractional minute renders as "12:2.2000000000000455",
    // and assigning that to <input type="time"> does not fail loudly — the browser
    // rejects the malformed string and leaves the field EMPTY.
    let m = Math.round(minutes)
    if (m >= 1440) m %= 1440
    const hours = Math.floor(m / 60)
    const mins = m % 60
    return String(hours).padStart(2, "0") + ":" + String(mins).padStart(2, "0")
  }

  readTimeValue(form, fieldSuffix) {
    // Time stepper (plus_minus): hidden field is source of truth
    const hidden = form.querySelector(
      `input[type="hidden"][name$="[${fieldSuffix}]"][data-time-stepper-target="hidden"]`,
    )
    if (hidden?.value) return hidden.value

    // Break time stepper
    const breakHidden = form.querySelector(
      `input[type="hidden"][name$="[${fieldSuffix}]"][data-break-time-stepper-target="hidden"]`,
    )
    if (breakHidden?.value) return breakHidden.value

    // Duration input (for break)
    const durationHidden = form.querySelector(
      `input[type="hidden"][name$="[${fieldSuffix}]"][data-duration-input-target="hidden"]`,
    )
    if (durationHidden?.value) return durationHidden.value

    // Break time minutes
    const breakMinutesHidden = form.querySelector(
      `input[type="hidden"][name$="[${fieldSuffix}]"][data-break-time-minutes-target="hidden"]`,
    )
    if (breakMinutesHidden?.value) return breakMinutesHidden.value

    // Native time input
    const timeInput = form.querySelector(`input[type="time"][name$="[${fieldSuffix}]"]`)
    if (timeInput?.value) return timeInput.value

    // Text input (timepicker)
    const textInput = form.querySelector(`input[type="text"][name$="[${fieldSuffix}]"]`)
    if (textInput?.value) return textInput.value

    // Plain hidden input (template default when the field is hidden by the template)
    const plainHidden = form.querySelector(`input[type="hidden"][name$="[${fieldSuffix}]"]`)
    if (plainHidden?.value) return plainHidden.value

    return null
  }

  parseTimeToMinutes(timeStr) {
    if (!timeStr) return 0
    const parts = timeStr.split(":")
    if (parts.length < 2) return 0
    return parseInt(parts[0], 10) * 60 + parseInt(parts[1], 10)
  }

  formatHours(hours) {
    const totalMinutes = Math.round(Math.abs(hours) * 60)
    if (this.durationFormatValue === "decimal") {
      // German decimal-comma hours, trimmed — matches SiteDrivingTimesHelper#german_decimal_hours
      const value = (totalMinutes / 60)
        .toFixed(2)
        .replace(/\.?0+$/, "")
        .replace(".", ",")
      return `${value}h`
    }
    const h = Math.floor(totalMinutes / 60)
    const m = totalMinutes % 60
    return m > 0 ? `${h}h ${m}min` : `${h}h`
  }

  rewriteClonedAllocation(clone, index) {
    const inputs = clone.querySelectorAll("input, select, textarea")
    inputs.forEach((input) => {
      if (input.name) {
        input.name = input.name.replace(/site_allocations\[\d+\]/, `site_allocations[${index}]`)
      }
      if (input.id) {
        input.id = input.id.replace(/site_allocations_\d+_/, `site_allocations_${index}_`)
      }
    })

    // Per-entry custom fields carry their own dynamic-form (conditional
    // visibility) scope — point the clone's scope at its own namespace.
    clone.querySelectorAll("[data-dynamic-form-name-prefix-value]").forEach((scope) => {
      scope.setAttribute(
        "data-dynamic-form-name-prefix-value",
        `site_allocations[${index}][custom_fields]`,
      )
    })

    // Update task wrapper IDs for site-tasks controller
    const taskWrapper = clone.querySelector("[id*='_task_ids_wrapper']")
    if (taskWrapper) {
      taskWrapper.id = `site_allocation_${index}_task_ids_wrapper`
    }

    const taskSelect = clone.querySelector("select[id*='_task_ids']")
    if (taskSelect) {
      taskSelect.id = `site_allocation_${index}_task_ids`
    }

    const siteSelect = clone.querySelector("select[data-model='site_allocation']")
    if (siteSelect) {
      siteSelect.dataset.wrapperId = `site_allocation_${index}_task_ids_wrapper`
      siteSelect.dataset.dropdownId = `site_allocation_${index}_task_ids`
    }

    const labels = clone.querySelectorAll("label[for]")
    labels.forEach((label) => {
      const forAttr = label.getAttribute("for")
      if (forAttr) {
        label.setAttribute(
          "for",
          forAttr.replace(/site_allocations_\d+_/, `site_allocations_${index}_`),
        )
      }
    })

    // The Fahrzeiten row <template> holds inert HTML the input loop above can't
    // see; rewrite its markup so rows added inside this clone are namespaced to
    // the new allocation index.
    clone.querySelectorAll("template").forEach((tpl) => {
      tpl.innerHTML = tpl.innerHTML.replace(
        /site_allocations\[\d+\]/g,
        `site_allocations[${index}]`,
      )
    })
  }

  clearAllocationValues(clone) {
    delete clone.dataset.autoCandidate

    // Cloned allocations start with no Fahrzeiten rows — each site's are independent.
    clone.querySelectorAll("[data-allocation-driving-row]").forEach((row) => row.remove())

    const selects = clone.querySelectorAll("select")
    selects.forEach((select) => {
      select.selectedIndex = 0
    })

    const clearableInputs = clone.querySelectorAll(
      "input[type='number'], input[type='text'], input[type='time'], input[type='date'], input[type='datetime-local']",
    )
    clearableInputs.forEach((input) => {
      input.value = ""
    })

    // Per-entry custom fields: clear textareas and uncheck checkbox /
    // multi-select boxes (their hidden "false" companions stay untouched).
    clone.querySelectorAll("textarea").forEach((textarea) => {
      textarea.value = ""
    })
    clone.querySelectorAll("input[type='checkbox']").forEach((checkbox) => {
      checkbox.checked = false
    })
    // A "Ganzer Tag" the source row had ticked locked its hours field; the clone's is unticked.
    clone.querySelectorAll("[data-allowance-input-target='hoursInput']").forEach((input) => {
      input.readOnly = false
    })

    clearPickerValueInputs(clone)

    const taskWrapper = clone.querySelector("[id*='_task_ids_wrapper']")
    if (taskWrapper) {
      taskWrapper.classList.add("hidden")
    }

    // Reset task-select siteId on cloned allocations
    const taskSelect = clone.querySelector("[data-controller*='task-select']")
    if (taskSelect) {
      taskSelect.dataset.taskSelectSiteIdValue = "0"
    }
  }

  resetSlimSelects(clone) {
    const ssWrappers = clone.querySelectorAll(".ss-main")
    ssWrappers.forEach((wrapper) => wrapper.remove())

    const selects = clone.querySelectorAll("select")
    selects.forEach((select) => {
      select.style.cssText = ""
      select.removeAttribute("tabindex")
      select.removeAttribute("aria-hidden")
      delete select.dataset.id
      select.classList.remove("ss-hide")
    })
  }

  reindexAllocations() {
    this.allocationTargets.forEach((allocation, idx) => {
      const inputs = allocation.querySelectorAll("input, select, textarea")
      inputs.forEach((input) => {
        if (input.name) {
          input.name = input.name.replace(/site_allocations\[\d+\]/, `site_allocations[${idx}]`)
        }
        if (input.id) {
          input.id = input.id.replace(/site_allocations_\d+_/, `site_allocations_${idx}_`)
        }
      })

      // Keep the per-entry custom fields' dynamic-form scope in sync.
      allocation.querySelectorAll("[data-dynamic-form-name-prefix-value]").forEach((scope) => {
        scope.setAttribute(
          "data-dynamic-form-name-prefix-value",
          `site_allocations[${idx}][custom_fields]`,
        )
      })

      const taskWrapper = allocation.querySelector("[id*='_task_ids_wrapper']")
      if (taskWrapper) {
        taskWrapper.id = `site_allocation_${idx}_task_ids_wrapper`
      }

      const taskSelect = allocation.querySelector("select[id*='_task_ids']")
      if (taskSelect) {
        taskSelect.id = `site_allocation_${idx}_task_ids`
      }

      const siteSelect = allocation.querySelector("select[data-model='site_allocation']")
      if (siteSelect) {
        siteSelect.dataset.wrapperId = `site_allocation_${idx}_task_ids_wrapper`
        siteSelect.dataset.dropdownId = `site_allocation_${idx}_task_ids`
      }

      const labels = allocation.querySelectorAll("label[for]")
      labels.forEach((label) => {
        const forAttr = label.getAttribute("for")
        if (forAttr) {
          label.setAttribute(
            "for",
            forAttr.replace(/site_allocations_\d+_/, `site_allocations_${idx}_`),
          )
        }
      })

      // Keep the Fahrzeiten row template in sync with the reindexed allocation.
      allocation.querySelectorAll("template").forEach((tpl) => {
        tpl.innerHTML = tpl.innerHTML.replace(
          /site_allocations\[\d+\]/g,
          `site_allocations[${idx}]`,
        )
      })
    })
  }
}
